import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigationStack } from '../contexts/NavigationContext';

interface NavigateWithStateOptions {
    from?: string;
    replace?: boolean;
    [key: string]: any;
}

export const useNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const navStack = useNavigationStack();
    const isInitializedRef = useRef(false);

    // ⭐ 페이지 진입 시 자동으로 스택에 현재 경로 등록
    useEffect(() => {
        const currentPath = location.pathname;

        if (!isInitializedRef.current) {
            const stack = navStack.getStack();
            if (stack.length === 0 || (stack.length === 1 && stack[0].path !== currentPath)) {
                // ⭐ 현재 location.state도 함께 저장
                navStack.push(currentPath, location.state || undefined);
            }
            isInitializedRef.current = true;
        }
    }, [location.pathname]);

    const currentFrom = location.state?.from;
    const currentState = location.state;

    // ⭐ 뒤로가기 - 이전 경로의 state까지 복원
    const goBack = useCallback((fallback?: string) => {
        const prevItem = navStack.pop();

        if (prevItem) {
            console.log('[useNavigation] goBack → 스택 pop:', prevItem.path, prevItem.state);
            navigate(prevItem.path, {
                replace: true,
                state: prevItem.state || null  // ⭐ 이전 페이지 state 복원
            });
        } else if (fallback) {
            console.log('[useNavigation] goBack → fallback:', fallback);
            navStack.reset(fallback);
            navigate(fallback, { replace: true });
        } else {
            console.log('[useNavigation] goBack → 홈으로');
            navStack.reset('/');
            navigate('/', { replace: true });
        }
    }, [navigate, navStack]);

    // ⭐ 페이지 이동 - 경로와 state를 스택에 함께 저장
    const navigateTo = useCallback((
        path: string,
        options?: NavigateWithStateOptions
    ) => {
        const { from, replace: isReplace, ...restState } = options || {};

        const stateToSave = {
            from: from !== undefined ? from : location.pathname,
            ...restState
        };

        console.log('[useNavigation] navigateTo:', path, stateToSave);

        if (isReplace) {
            // ⭐ state도 함께 저장
            navStack.replaceTop(path, stateToSave);
        } else {
            // ⭐ state도 함께 저장
            navStack.push(path, stateToSave);
        }

        navigate(path, {
            replace: isReplace,
            state: stateToSave
        });
    }, [navigate, location.pathname, navStack]);

    // ⭐ 이전 페이지의 state 유지하며 이동
    const navigateWithParentState = useCallback((
        path: string,
        additionalState?: Record<string, any>
    ) => {
        const stateToSave = {
            ...location.state,
            ...additionalState
        };
        navStack.push(path, stateToSave);
        navigate(path, { state: stateToSave });
    }, [navigate, location.state, navStack]);

    // ⭐ replace 모드로 이동
    const navigateReplace = useCallback((
        path: string,
        state?: Record<string, any>
    ) => {
        navStack.replaceTop(path, state);
        navigate(path, {
            replace: true,
            state
        });
    }, [navigate, navStack]);

    return {
        goBack,
        navigateTo,
        navigateWithParentState,
        navigateReplace,
        currentFrom,
        currentState,
        canGoBack: navStack.canGoBack(),
        getStack: navStack.getStack,
        resetStack: navStack.reset,
    };
};